import { t as tr } from './i18n';
import type { AttentionItem, AttentionKind } from './cockpit';
import { lei } from './format';

/**
 * Grupurile din „Necesită atenție”, aceleași pentru panou și pentru mailul de
 * dimineață: ce intră în fiecare, în ce ordine, cu ce titlu și ce buton. Panoul
 * adaugă doar acțiunile (dialogurile); mailul doar linkurile.
 */

export type GroupKey = 'overdue' | 'late' | 'decide' | 'approve' | 'order' | 'offers' | 'site' | 'req';

export interface GroupDef {
  key: GroupKey;
  urgency: 'urgent' | 'action';
  tone: 'red' | 'orange' | 'yellow' | 'blue' | 'gray';
  /** „2 livrări întârziate” */
  title: (n: number) => string;
  /** butonul grupului, cu aceleași cuvinte în panou și în mail */
  cta: string;
}

const pl = (n: number, one: string, many: string): string => `${n} ${n === 1 ? one : many}`;

export const ATTENTION_GROUPS: readonly GroupDef[] = [
  {
    key: 'overdue',
    urgency: 'urgent',
    tone: 'red',
    get cta() {
      return tr('Follow-up');
    },
    title: (n) => pl(n, tr('răspuns de la furnizor întârziat'), tr('răspunsuri de la furnizori întârziate')),
  },
  {
    key: 'late',
    urgency: 'urgent',
    tone: 'red',
    get cta() {
      return tr('Sună furnizorul');
    },
    title: (n) => pl(n, tr('livrare întârziată'), tr('livrări întârziate')),
  },
  {
    key: 'decide',
    urgency: 'action',
    tone: 'orange',
    get cta() {
      return tr('Compară');
    },
    title: (n) => pl(n, tr('comparație gata'), tr('comparații gata')),
  },
  {
    key: 'approve',
    urgency: 'action',
    tone: 'yellow',
    get cta() {
      return tr('Aprobă');
    },
    title: (n) => pl(n, tr('comandă așteaptă aprobarea ta'), tr('comenzi așteaptă aprobarea ta')),
  },
  {
    key: 'order',
    urgency: 'action',
    tone: 'blue',
    get cta() {
      return tr('Fă comanda');
    },
    title: (n) => pl(n, tr('decizie fără comandă'), tr('decizii fără comandă')),
  },
  {
    key: 'offers',
    urgency: 'action',
    tone: 'yellow',
    get cta() {
      return tr('Verifică');
    },
    title: (n) => pl(n, tr('ofertă de verificat'), tr('oferte de verificat')),
  },
  {
    key: 'site',
    urgency: 'action',
    tone: 'yellow',
    get cta() {
      return tr('Verifică');
    },
    title: (n) => pl(n, tr('termen pe șantier'), tr('termene pe șantier')),
  },
  {
    key: 'req',
    urgency: 'action',
    tone: 'gray',
    get cta() {
      return tr('Preia');
    },
    title: (n) => tr('{0} de pe șantier', pl(n, tr('necesar nou'), tr('necesare noi'))),
  },
];

/** Unde duce un rând: aceleași locuri ca linkurile din mail (?cerere, ?comanda, ?necesar). */
export type RowTarget =
  | { kind: 'rfq'; rfqId: string; followUp?: string }
  | { kind: 'order'; rfqId: string; orderId: string }
  | { kind: 'requisition'; id: string };

export interface AttentionRow {
  key: string;
  kind: AttentionKind | 'decide' | 'order' | 'req';
  group: GroupKey;
  title: string;
  detail: string;
  /** butonul rândului */
  cta: string;
  target: RowTarget;
  /** furnizorul la care se face follow-up-ul sau întrebarea */
  sendId?: string;
}

export interface AttentionSource {
  /** `attention(...)` din cockpit */
  alerts: AttentionItem[];
  /** `todayView(...).decide`, cu cel mai bun total al cererii (din panou) */
  decide: {
    rfq: { id: string; title: string; site: { name: string } };
    offers: number;
    waiting: number;
    best: number | null;
  }[];
  toOrder: {
    rfq: { id: string; title: string; site: { name: string } };
    supplier: { name: string };
    total: number | null;
  }[];
  requisitions: { id: string; number: string; title: string; siteName: string; lines: number }[];
}

const GROUP_OF: Record<AttentionKind, GroupKey> = {
  silent: 'overdue',
  rfqLate: 'overdue',
  orderLate: 'late',
  partial: 'offers',
  expired: 'offers',
  expiring: 'offers',
  ask: 'offers',
  neededSoon: 'site',
  rentOver: 'site',
};

const ctaOf = (g: GroupKey): string => ATTENTION_GROUPS.find((d) => d.key === g)!.cta;

/** Rândurile din „Necesită atenție”, în ordinea grupurilor. Aprobările nu intră: sunt pe om. */
export function attentionRows(src: AttentionSource): AttentionRow[] {
  const rows: AttentionRow[] = [];
  for (const a of src.alerts) {
    const group = GROUP_OF[a.kind];
    rows.push({
      key: a.key,
      kind: a.kind,
      group,
      title: a.title,
      detail: a.detail,
      cta: a.kind === 'ask' ? tr('Întreabă') : ctaOf(group),
      target: a.orderId
        ? { kind: 'order', rfqId: a.rfqId, orderId: a.orderId }
        : a.kind === 'silent' && a.sendId
          ? { kind: 'rfq', rfqId: a.rfqId, followUp: a.sendId }
          : { kind: 'rfq', rfqId: a.rfqId },
      ...(a.sendId ? { sendId: a.sendId } : {}),
    });
  }
  for (const d of src.decide) {
    rows.push({
      key: `d-${d.rfq.id}`,
      kind: 'decide',
      group: 'decide',
      title: d.rfq.title,
      detail: tr(
        '{0} · {1} din {2} oferte{3}',
        d.rfq.site.name,
        d.offers,
        d.offers + d.waiting,
        d.best ? tr(' · de la {0}', lei(d.best)) : '',
      ),
      cta: ctaOf('decide'),
      target: { kind: 'rfq', rfqId: d.rfq.id },
    });
  }
  for (const o of src.toOrder) {
    rows.push({
      key: `o-${o.rfq.id}-${o.supplier.name}`,
      kind: 'order',
      group: 'order',
      title: o.rfq.title,
      detail: `${o.supplier.name}${o.total != null ? ` · ${lei(o.total)}` : ''} · ${o.rfq.site.name}`,
      cta: ctaOf('order'),
      target: { kind: 'rfq', rfqId: o.rfq.id },
    });
  }
  for (const q of src.requisitions) {
    rows.push({
      key: `q-${q.id}`,
      kind: 'req',
      group: 'req',
      title: `${q.number} · ${q.title}`,
      detail: `${q.siteName} · ${pl(q.lines, 'material', 'materiale')}`,
      cta: ctaOf('req'),
      target: { kind: 'requisition', id: q.id },
    });
  }
  const order = ATTENTION_GROUPS.map((g) => g.key);
  return rows
    .map((r, i) => ({ r, i }))
    .sort((a, b) => order.indexOf(a.r.group) - order.indexOf(b.r.group) || a.i - b.i)
    .map((x) => x.r);
}

/** Câte rânduri încap în mailul de dimineață; restul rămân în aplicație. */
export const MORNING_MAX_ROWS = 8;

const urgentGroups = new Set(ATTENTION_GROUPS.filter((g) => g.urgency === 'urgent').map((g) => g.key));

/** Ce intră în mail: întâi urgentele, cel mult `max`, și câte au rămas pe dinafară. */
export function morningList(
  rows: AttentionRow[],
  max = MORNING_MAX_ROWS,
): { shown: AttentionRow[]; more: number; urgent: number; total: number } {
  const urgent = rows.filter((r) => urgentGroups.has(r.group));
  const rest = rows.filter((r) => !urgentGroups.has(r.group));
  const shown = [...urgent, ...rest].slice(0, max);
  return { shown, more: rows.length - shown.length, urgent: urgent.length, total: rows.length };
}

export const isUrgent = (r: AttentionRow): boolean => urgentGroups.has(r.group);
